import { type Block } from '@ttab/elephant-api/newsdoc'
import type * as views from '@/views'
import { type LucideIcon } from '@ttab/elephant-ui/icons'
import type { TemplatePayload } from '@/shared/templates'
import type { Locale } from 'date-fns'
import type { RefObject } from 'react'

export enum NavigationActionType {
  SET = 'set',
  FOCUS = 'focus',
  ACTIVE = 'active',
  ON_DOC_CREATED = 'onDocumentCreated'
}

export type View = keyof typeof views

export interface NavigationAction {
  type: NavigationActionType
  props?: ViewProps
  content?: ContentState[]
  viewId?: string
  active?: string
  callback?: (() => void) | undefined
}

interface ViewWidths {
  [key: string]: number // FIXME: Should use some keyof typeof thingy...
  sm: number
  md: number
  lg: number
  xl: number
  '2xl': number
  hd: number
  fhd: number
  qhd: number
  uhd: number
}


export interface ViewMetadata {
  name: ContentState['name']
  path: ContentState['path']
  widths: ViewWidths
}

export interface ViewRegistryItem {
  meta: ViewMetadata
  component: React.FC<ViewProps>
}

export interface ViewRegistry {
  get: (key: View) => ViewRegistryItem
  set: () => void
}

export interface NavigationState {
  viewRegistry: ViewRegistry
  focus: string | null
  active: string | undefined
  content: ContentState[]
}

export interface ContentState {
  viewId: string
  name: View
  path: string
  props?: ViewProps
}

export interface ViewProps {
  id?: string | null
  planningId?: string | null
  version?: string
  asDialog?: boolean
  onDialogClose?: (id?: string, title?: string) => void
  onDocumentCreated?: () => void
  from?: string
  source?: string
  className?: string
  payload?: TemplatePayload
  autoFocus?: boolean
  setOpen?: (open?: boolean) => boolean
  preview?: boolean
}

export type Theme = 'dark' | 'light' | 'system'

export interface ThemeProviderState {
  theme: Theme
  setTheme: (theme: Theme) => void
}

export interface ViewProviderState {
  viewId: string
  name: string
  isActive: boolean
  isFocused: boolean
  isHidden: boolean
}

export interface DefaultValueOption {
  payload?: Block
  label: string
  value: string
  icon?: LucideIcon
  iconProps?: {
    size?: number
    fill?: string
    color?: string
    strokeWidth?: number
    className?: string
  }
  color?: string
  info?: string
}


export type ValidateStateRef = RefObject<ValidateState>

export type ValidateState = Record<string, {
  label: string
  valid: boolean
  reason: string
}>

export type LocaleData = {
  module: Locale
  code: {
    short: string
    long: string
    full: string
  }
}

export interface QueryType {
  query: {
    bool: {
      should: unknown[]
      must: unknown[]
    }
  }
  _source?: boolean
  fields?: string[]
  sort?: Array<Record<string, 'asc' | 'desc'>>
}

export interface PlanningData {
  end_date: string
  public: string
  start_date: string
  tentative: string
}

export interface StatusMeta {
  created: string
  creator: string
  id: bigint
  meta: object
  metaDocVersion: bigint
  version: bigint
}

export interface StatusData {
  heads: {
    usable?: StatusMeta
    done?: StatusMeta
    approved?: StatusMeta
    withheld?: StatusMeta
    cancelled?: StatusMeta
  }
  modified: string
  uuid: string
  version: string
  workflowState: string
  workflowCheckpoint: string
}
